import _ from 'lodash';
import * as Sentry from '@sentry/react';

import { RestService } from './api.service';
import { LoginFormFields } from '../../components/User/LoginForm/login-form.interfaces';
import { ENABLE_SENTRY, SUPPORT_EMAIL } from '../../../_configuration';
import { initializeStorage, store } from '../utils/storage';
import { STAFF_TOKEN_KEY, PARENT_TOKEN_KEY } from '../services/auth.service';

// HTTP status the server returns for an unrecoverable bad/expired token —
// the signal that the persisted credential must be cleared from localStorage
// so subsequent boots stop re-attempting (and re-failing) the restore.
const TOKEN_REJECTED_STATUS = 401;
// "The refresh never got an answer", distinct from "the credential is dead". Only the latter may
// end a session; a server or network failure must leave the user signed in.
export const REFRESH_RETRY = 'retry' as const;

interface TokenAuth {
  token: string;
}

export class AuthApiService {
  static LOGIN_ENDPOINT = '/login';
  static REFRESH_TOKEN_ENDPOINT = '/token/restore-session';
  static TOKEN_AUTH_ENDPOINT = '/token/login';
  static STAFF_TOKEN_REFRESH_ENDPOINT = '/token/refresh';

  static async authenticate({ email, password }: LoginFormFields) {
    try {
      const response = await RestService.post(AuthApiService.LOGIN_ENDPOINT, {
        email: email?.toLowerCase(),
        password
      });

      if (response.status === 401) {
        throw new Error('You entered an invalid email or password');
      } else if (response.status) {
        throw new Error(`Something went wrong. Contact ${SUPPORT_EMAIL} for assistance`);
      } else {
        return _.get(response, 'user');
      }
    } catch (err) {
      if (err.message === 'You entered an invalid email or password') {
        throw err;
      }

      let message = `Something went wrong. Contact ${SUPPORT_EMAIL} for assistance`;

      if (_.get(err, 'response.status') === 401) {
        message = 'You entered an invalid email or password';
      }

      throw new Error(message);
    }
  }

  static async authenticateWithToken({ token }: TokenAuth) {
    try {
      const response = await RestService.post(AuthApiService.TOKEN_AUTH_ENDPOINT, { token });

      if (response.status === 401) {
        throw new Error('Authentication link has expired');
      } else if (response.status) {
        throw new Error(`Something went wrong. Contact ${SUPPORT_EMAIL} for assistance`);
      } else {
        return _.get(response, 'user');
      }
    } catch (err) {
      throw new Error(`Something went wrong. Contact ${SUPPORT_EMAIL} for assistance`);
    }
  }

  // Restores a single persisted token. See CLAUDE.md →
  // "Token restore semantics" for the full outcome table.
  static async restoreOne(token: string, storageKey: string) {
    try {
      const response = await RestService.post(AuthApiService.REFRESH_TOKEN_ENDPOINT, { token }, token);
      const user = _.get(response, 'user');
      if (user) {
        if (user.token && user.token !== token) {
          await store.set(storageKey, user.token);
        }
        return user;
      }

      // No `user` on a 200 body (status undefined for parsed JSON) is the same
      // signal as an explicit 401: the credential is no longer valid. Clear it.
      const status = (response as Response | undefined)?.status;
      if (status === undefined || status === TOKEN_REJECTED_STATUS) {
        await store.remove(storageKey);
      }
      return null;
    } catch (err) {
      if (ENABLE_SENTRY) Sentry.captureException(err);
      throw err;
    }
  }

  // Trades the httpOnly refresh cookie set at staff login for a new token. Null means the
  // credential itself is dead (cookie gone or refused) and the caller ends the session.
  // REFRESH_RETRY means the call never got an answer, which must not destroy a live session.
  static async refreshStaffToken(): Promise<string | null | typeof REFRESH_RETRY> {
    try {
      const response = await RestService.post(AuthApiService.STAFF_TOKEN_REFRESH_ENDPOINT);
      const token = _.get(response, 'token');
      if (token) {
        await store.set(STAFF_TOKEN_KEY, token);
        return token;
      }

      // A parsed 200 body leaves status undefined; anything else is the raw Response. Only a 401
      // is the server saying the credential is dead — a 500 or 502 is the server being unwell.
      const status = (response as Response | undefined)?.status;
      return status === undefined || status === TOKEN_REJECTED_STATUS ? null : REFRESH_RETRY;
    } catch (err) {
      // Network error: the server never answered, so it never rejected the credential.
      console.warn('refreshStaffToken failed:', err);
      return REFRESH_RETRY;
    }
  }

  // Pre-split clients persisted a single staff-only token under `mea_token`
  // (now STAFF_TOKEN_KEY). The post-split layout kept that same key for the
  // staff slot, so a legacy single-token install is naturally restored as a
  // staff session — no migration is needed. The server's refreshSession
  // mirrors this on the JWT side: any token whose `sessionType` claim is
  // missing falls through to the staff path. Don't add a new "legacy" key
  // or branch here; the silent reuse of `mea_token` IS the migration.
  static async refreshSession() {
    await initializeStorage();

    const staffToken = await store.get(STAFF_TOKEN_KEY);
    const parentToken = await store.get(PARENT_TOKEN_KEY);

    const [staff, parent] = await Promise.all([
      staffToken ? AuthApiService.restoreOne(staffToken, STAFF_TOKEN_KEY) : Promise.resolve(null),
      parentToken ? AuthApiService.restoreOne(parentToken, PARENT_TOKEN_KEY) : Promise.resolve(null),
    ]);

    return { staff, parent };
  }
}
